import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import Modal from "@/components/ui/Modal";
import { FormField, Input, Textarea, Select } from "@/components/ui/FormComponents";
import { UPDATE_OPTIONS } from "../constants/projectConstants";
import { projectService } from "../services/projectService";
import { ROLE_LABELS } from '../../verve-teams/utils/teamMemberLabels';

export const TradeWorkUpdateModal = ({ isOpen, onClose, project, trade, onSuccess, setToast }) => {
  const activeTrades = Array.from(new Set(
    (project?.fieldOfficers || [])
      .filter(fo => typeof fo === 'object')
      .map(fo => fo.installationTrade || fo.role)
      .filter(Boolean)
  ));

  const [selectedTrade, setSelectedTrade] = useState(trade || activeTrades[0] || "general");
  const [updateStatus, setUpdateStatus] = useState("");
  const [workStartTime, setWorkStartTime] = useState("");
  const [workEndTime, setWorkEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && project) {
      const initialTrade = trade || activeTrades[0] || "general";
      setSelectedTrade(initialTrade);
      const currentMilestones = project.installationMilestones || {};
      setProgress(currentMilestones[`${initialTrade}Progress`] || 0);
      setUpdateStatus("");
      setWorkStartTime("");
      setWorkEndTime("");
      setDescription("");
      setPhotos([]);
      setError("");
    }
  }, [isOpen, project, trade]);

  // Update progress when selectedTrade changes
  useEffect(() => {
    if (project && selectedTrade) {
      const currentMilestones = project.installationMilestones || {};
      setProgress(currentMilestones[`${selectedTrade}Progress`] || 0);
    }
  }, [selectedTrade, project]);

  // Auto-update progress based on status/stages
  useEffect(() => {
    if (updateStatus === 'COMPLETED') {
      setProgress(100);
    } else if (updateStatus === 'STARTED') {
      setProgress(20);
    } else if (updateStatus === 'IN_PROGRESS') {
      setProgress(40);
    } else if (updateStatus === 'ON_HOLD') {
      setProgress(40);
    } else if (updateStatus === 'WAITING_MATERIAL') {
      setProgress(80);
    } else if (!updateStatus) {
      setProgress(0);
    }
  }, [updateStatus]);

  if (!isOpen || !project) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!updateStatus) {
      setError("Please select an update status.");
      return;
    }
    setSubmitting(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("trade", selectedTrade);
      formData.append("updateStatus", updateStatus);
      formData.append("title", updateStatus); // Default title to status
      formData.append("description", description.trim());
      formData.append("progress", updateStatus === 'COMPLETED' ? 100 : progress);
      formData.append("workStartTime", workStartTime || "");
      formData.append("workEndTime", workEndTime || "");

      if (photos && photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          formData.append("images", photos[i]);
        }
      }

      const targetId = project._id || project.id || project.customId || project.verveId;
      await projectService.postTradeWorkUpdate(targetId, formData);

      setToast({ message: "Work update posted successfully!", type: "success" });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Failed to post work update.");
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle = (
    <div className="flex flex-col min-w-0">
      <h2 className="text-[16px] font-semibold text-slate-800 tracking-tight leading-none">
        Add Work Update
      </h2>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-end gap-3 w-full">
      <button
        type="button"
        onClick={onClose}
        className="px-6 py-2.5 bg-white border border-slate-200 text-slate-500 rounded-md cursor-pointer text-[12px] font-semibold tracking-wider hover:bg-slate-50 transition-all shadow-sm"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="px-8 py-2.5 bg-[#64748b] hover:bg-[#475569] cursor-pointer text-white rounded-md text-[12px] font-semibold tracking-wider transition-all disabled:opacity-50 flex items-center gap-3 shadow-md shadow-blue-200"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Update"}
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} footer={modalFooter} width="max-w-[560px]">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5 font-poppins">
        {error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-[11px] font-bold text-rose-700 animate-in fade-in slide-in-from-top-1">
            {error}
          </div>
        )}

        <FormField label="Role type" required>
          <Select
            value={selectedTrade}
            onChange={(e) => setSelectedTrade(e.target.value)}
            className="text-[13px] font-medium capitalize"
          >
            {activeTrades.length > 0 ? (
              activeTrades.map(tradeValue => {
                const label = ROLE_LABELS[tradeValue] || tradeValue.charAt(0).toUpperCase() + tradeValue.slice(1).replace(/_/g, ' ');
                return <option key={tradeValue} value={tradeValue}>{label}</option>
              })
            ) : (
              <option value="general">General Update</option>
            )}
          </Select>
        </FormField>

        <FormField label="Update status" required>
          <Select
            value={updateStatus}
            onChange={(e) => setUpdateStatus(e.target.value)}
            className="text-[13px] font-medium"
          >
            <option value="" disabled>Select status...</option>
            {UPDATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Work start time">
            <Input
              type="datetime-local"
              value={workStartTime}
              onChange={(e) => setWorkStartTime(e.target.value)}
              className="text-[13px] font-medium h-[38px]"
            />
          </FormField>
          <FormField label="Work end time">
            <Input
              type="datetime-local"
              value={workEndTime}
              onChange={(e) => setWorkEndTime(e.target.value)}
              className="text-[13px] font-medium h-[38px]"
            />
          </FormField>
        </div>

        <FormField label="Description">
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what was done..."
            className="h-[72px] min-h-[72px] text-[13px] font-medium leading-relaxed resize-none"
          />
        </FormField>

        <FormField label="Photos (Optional)">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setPhotos(Array.from(e.target.files))}
            className="text-[12px] font-medium text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-[12px] file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </FormField>

        <FormField label="Progress" required>
          <div className="flex flex-col gap-2">
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              disabled={updateStatus === "COMPLETED"}
              className={`w-full h-1.5 rounded-full appearance-none cursor-pointer transition-all accent-[#2b448f] ${updateStatus === "COMPLETED" ? "bg-[#2b448f]" : "bg-slate-100"
                }`}
            />
            <div className="flex justify-between text-[10px] font-bold text-slate-400 mt-1">
              <span>0%</span>
              <span className="text-[#2b448f] font-bold">{progress}% Complete</span>
              <span>100%</span>
            </div>
          </div>
        </FormField>
      </form>
    </Modal>
  );
};
